'use client';

import { LOCALES, LOCALE_LABELS, NEXT_LOCALE } from '@/constants/locale';
import type { Locale } from '@/types/dictionary';
import { useCallback } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useTranslations from '@/hooks/useTranslations';
import useAuth from '@/lib/firebase/auth/store';
import updateUser from '@/actions/user/updateUser';

export default function SelectLanguage({
  sx,
  showLabel = true,
  isMarketingWebsite = false,
}: {
  sx?: any;
  showLabel?: boolean;
  isMarketingWebsite?: boolean;
}) {
  const { locale, t } = useTranslations();
  const { userId } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const handleChange = useCallback(
    async (e: SelectChangeEvent) => {
      try {
        const newLocale = e.target.value as Locale;

        // Set cookie
        document.cookie = `${NEXT_LOCALE}=${newLocale}; path=/;`;

        // Replace locale in pathname
        // Handles: /en, /en/about, /en/about/page, etc.
        const newPathname = pathname.replace(/^\/[a-z]{2}(?=\/|$)/, `/${newLocale}`);

        // Copy search params
        const newSearchParams = new URLSearchParams(searchParams);

        // Update user locale if authenticated
        if (userId) {
          try {
            await updateUser({ id: userId, values: { locale: newLocale } });
          } catch (error) {
            console.error('Failed to update user locale:', error);
            // Continue with navigation even if user update fails
          }
        }

        // Build full URL
        const fullUrl = `${newPathname}?${newSearchParams.toString()}`;
        console.log('Navigating to:', fullUrl);

        router.push(fullUrl);
      } catch (error) {
        console.error('Language switch failed:', error);
      }
    },
    [pathname, router, searchParams, userId],
  );

  return (
    <Stack
      direction='row'
      sx={[
        {
          alignItems: 'center',
          justifyContent: 'space-between',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {showLabel && (
        <Typography
          variant='body2'
          sx={{
            color: 'text.primary',
          }}
        >
          {t('common.language')}
        </Typography>
      )}

      <Select
        value={locale}
        size='small'
        variant={isMarketingWebsite ? 'standard' : 'outlined'}
        {...(isMarketingWebsite && { disableUnderline: true })}
        onChange={handleChange}
        sx={{
          '& .MuiSelect-select': {
            px: 0.4,
            py: 0.125,
            fontSize: '0.875rem',
          },
        }}
      >
        {LOCALES.map((locale) => (
          <MenuItem key={locale} value={locale} dense>
            {`${LOCALE_LABELS[locale]}`}
          </MenuItem>
        ))}
      </Select>
    </Stack>
  );
}
